import { MarketDates, TextField } from '../../runtypes/VendorRuntype';
import { OptionsMap } from '../../support/helpers/CategoryOptions';
import { localeIncludes } from '../../support/localeIncludes';
import { objectMap } from '../../support/objectMap';
import getShortVendorId from '../../support/shared/getShortVendorId';

import { GetVendorList_season_vendors_markets as Market } from './__generated__/GetVendorList';

type Tag = {
  id: string;
};

type Vendor = {
  id: string;
  name: string;
  contact: string;
  email: string;
  category: Array<string>;
  status: string;
  tags: Array<Tag>;
  markets: Array<Market>;
  textFields?: Array<TextField> | null;
};

export function filterVendors<T extends Vendor>(input: {
  vendors: Array<T>;
  filters: {
    searchText: string;
    selectedCategories: Set<string>;
    selectedStatuses: Set<string>;
    selectedTags: Set<string>;
    selectedMarketDates?: MarketDates;
  };
  optionsMap: OptionsMap;
}) {
  const { filters, optionsMap, vendors } = input;
  const { selectedCategories, selectedStatuses, selectedTags } = filters;
  const searchText = filters.searchText.toLowerCase().trim();
  const selectedMarketDates = objectMap(
    filters.selectedMarketDates ?? {},
    (id, dates) => new Set(dates),
  );
  const numSelected = Object.values(selectedMarketDates).reduce(
    (total, set) => total + set.size,
    0,
  );
  return vendors.filter(
    (vendor) =>
      matchesCategories({ vendor, selectedCategories, optionsMap }) &&
      matchesStatuses(vendor, selectedStatuses) &&
      matchesTags(vendor, selectedTags) &&
      matchesSearch(vendor, searchText) &&
      (vendor.markets !== undefined
        ? matchesMarketDates(vendor, numSelected, selectedMarketDates)
        : true),
  );
}

function matchesCategories(input: {
  vendor: Vendor;
  selectedCategories: Set<string>;
  optionsMap: OptionsMap;
}) {
  const { optionsMap, selectedCategories, vendor } = input;
  if (selectedCategories.size === 0) {
    return true;
  }
  return vendor.category.some((id) =>
    selectedCategories.has(optionsMap.get(id)?.name ?? ''),
  );
}

function matchesStatuses(vendor: Vendor, selectedStatuses: Set<string>) {
  if (selectedStatuses.size === 0) {
    return true;
  }
  return selectedStatuses.has(vendor.status);
}

function matchesTags(vendor: Vendor, selectedTags: Set<string>) {
  if (selectedTags.size === 0) {
    return true;
  }
  const vendorTags = new Set(vendor.tags.map(({ id }) => id));
  for (const tag of selectedTags) {
    if (!vendorTags.has(tag)) {
      return false;
    }
  }
  return true;
}

function matchesSearch(vendor: Vendor, searchText: string) {
  if (searchText === '') {
    return true;
  }
  for (const key of ['id', 'name', 'contact', 'email'] as const) {
    const value = typeof vendor[key] === 'string' ? vendor[key] : '';
    if (localeIncludes(value, searchText)) {
      return true;
    } else if (key === 'id') {
      const shortVendorId = getShortVendorId(vendor.id);
      if (searchText === shortVendorId) {
        return true;
      }
    }
  }
  // Only activating text field searching after 3 characters, otherwise when searching between 1-2 characters, most vendors will still show up which defeats the purpose of the search
  const searchTextLengthThreshold = 3;
  const searchTextLengthMeetsThreshold =
    searchText.length >= searchTextLengthThreshold;
  const textFieldValues = vendor.textFields?.map((field) => field.value);
  for (const value of textFieldValues ?? []) {
    if (localeIncludes(value, searchText) && searchTextLengthMeetsThreshold) {
      return true;
    }
  }
  return false;
}

export function matchesMarketDates(
  vendor: Vendor,
  numSelected: number,
  selectedMarketDates: ObjectOf<Set<string>>,
) {
  if (numSelected === 0) {
    return true;
  }
  for (const { id, dates } of vendor.markets ?? []) {
    const selectedDates = selectedMarketDates[id];
    if (selectedDates) {
      for (const date of dates) {
        if (selectedDates.has(date)) {
          return true;
        }
      }
    }
  }
  return false;
}
